import {
  CalendarOutlined,
  DollarOutlined,
  EyeOutlined,
  FireOutlined,
  FlagOutlined,
  TagsOutlined,
  TrophyOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useLazyQuery, useQuery } from '@apollo/client';
import {
  Challenge,
  checkPermission,
  DashboardChallengeIdeaStats,
  GET_DASHBOARD_CHALLENGE_IDEA_BUSINESS_AREAS,
  GET_ME,
  PERMISSIONS,
} from '@frontend/data-access';
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { IconCard, SocialShareButtonsGroup } from '@frontend/shared/ui';
import {
  capFirst,
  formatChallengeDateWithTimezone,
  formatDate,
} from '@frontend/shared/util';
import { Col, Row, Space, Typography } from 'antd';
import { formatDistance, isBefore } from 'date-fns';
import useTranslation from 'next-translate/useTranslation';
import { useEffect } from 'react';

export interface ChallengeBasicsProps {
  challenge: Challenge;
  showShareButtons?: boolean;
}

export const getCountdown = (
  registrationStartAt: string,
  registrationEndAt: string
) => {
  const isBeforeDeadline = isBefore(new Date(), new Date(registrationEndAt));

  if (isBeforeDeadline) {
    return (
      <>
        {' '}
        &#8212; {formatDistance(new Date(), new Date(registrationEndAt))} left
      </>
    );
  } else {
    return '';
  }
};

export function ChallengeBasics({
  challenge,
  showShareButtons = true,
}: ChallengeBasicsProps) {
  const { t } = useTranslation('admin');
  const { t: tcommon } = useTranslation('common');
  const { data } = useQuery(GET_ME);

  const [getData, { data: businessArea }] = useLazyQuery<{
    dashboardChallengeIdeasByAreas: DashboardChallengeIdeaStats[];
  }>(GET_DASHBOARD_CHALLENGE_IDEA_BUSINESS_AREAS);

  useEffect(() => {
    if (challenge?.__typename === 'InternalChallenge') {
      getData({
        variables: {
          challengeId: challenge.id,
        },
      });
    }
  }, [getData, challenge]);

  const renderPrizes = () => {
    return (
      <div className="flex flex-col">
        {Number(challenge.prizeCashAmount) ? (
          <span>
            {Number(challenge.prizeCashAmount).toLocaleString('en-US') + '$'}
          </span>
        ) : (
          ''
        )}
        {challenge.prizes
          ?.filter((prize) => !['CASH', 'CUSTOM'].includes(prize))
          .map((prize, index) => {
            return (
              <span key={index}>
                {prize === 'SCV_PARTNERSHIP'
                  ? 'Opportunity to partner with Ignyte'
                  : prize}
              </span>
            );
          })}
      </div>
    );
  };

  const getStatus = () => {
    let statusText: string;
    let statusClass: string;

    switch (challenge.state) {
      case 'ACTIVE':
        statusClass = 'bg-green-100 text-green-600';
        statusText = tcommon('Registration open');
        break;
      case 'UPCOMING':
        statusClass = 'bg-gold-100 text-gold-600';
        statusText = tcommon('Upcoming challenge');
        break;
      case 'ENDED':
        statusClass = 'bg-gray-300 text-gray-800';
        statusText = tcommon('Registration closed');
        break;
      case 'DRAFT':
        statusClass = 'bg-gray-100 text-gray-600';
        statusText = tcommon('Draft challenge');
        break;
      case 'ARCHIVED':
        statusClass = 'bg-red-100 text-red-600';
        statusText = tcommon('Archived challenge');
        break;
      default:
        statusClass = '';
        statusText = '';
        break;
    }

    return (
      <div className={`w-min truncate rounded-[20px] py-1 px-3 ${statusClass}`}>
        <span className="font-medium text-xs" data-test="challenge-status">
          {statusText}
        </span>
      </div>
    );
  };

  return (
    <Space className="w-full" direction="vertical" size="middle">
      {challenge?.state && (
        <IconCard
          title={
            <div className="flex items-center justify-between gap-2">
              {t('intra_challenge_basics_state')}
              {getStatus()}
            </div>
          }
          icon={<EyeOutlined />}
        >
          <div />
        </IconCard>
      )}
      <IconCard
        title={
          challenge.registrationEndedAt
            ? t('intra_challenge_basics_registration_ended')
            : t('intra_challenge_basics_registration_deadline')
        }
        icon={<CalendarOutlined />}
      >
        {!challenge.registrationEndedAt && (
          <Typography.Text>
            {formatChallengeDateWithTimezone(
              challenge?.registrationEndAt,
              challenge?.timezone,
              'DD MMMM YYYY, HH:mm'
            )}
          </Typography.Text>
        )}
      </IconCard>
      {(data?.me?.role === 'ADMIN' ||
        checkPermission(data?.me, [
          PERMISSIONS.INTERNAL_CHALLENGE_MANAGEMENT,
        ])) && (
        <IconCard
          title={t('intra_challenge_basics_registration_created_by')}
          icon={<UserOutlined />}
        >
          <Typography.Text className="capitalize">
            {challenge?.createdBy?.fullName || '-'}
          </Typography.Text>
        </IconCard>
      )}
      {challenge.__typename === 'OpenChallenge' && (
        <IconCard
          title={t('intra_challenge_basics_registration_prize')}
          icon={<DollarOutlined />}
        >
          <Typography.Text>{renderPrizes()}</Typography.Text>
        </IconCard>
      )}
      {challenge.__typename === 'InternalChallenge' && (
        <IconCard
          title={t('intra_challenge_basics_registration_open_tp')}
          icon={<UserOutlined />}
        >
          <Typography.Text>
            {capFirst(challenge?.participationType)}
          </Typography.Text>
        </IconCard>
      )}
      <IconCard
        title={t('intra_challenge_basics_registration_sponsor')}
        icon={<TrophyOutlined />}
      >
        <Typography.Text>{challenge?.sponsor}</Typography.Text>
      </IconCard>
      {challenge?.innovationSpaceTag?.name && (
        <IconCard
          title={t('intra_challenge_basics_registration_innovation_space')}
          icon={<FireOutlined />}
        >
          <Typography.Text>
            {challenge?.innovationSpaceTag?.name}
          </Typography.Text>
        </IconCard>
      )}
      <IconCard title={tcommon('Regions')} icon={<FlagOutlined />}>
        <Row className="gap-2 mt-3">
          {challenge?.regions?.map((region, index) => {
            return (
              <Col
                className="bg-gray-200 py-1 px-3 rounded-full text-xs"
                key={index}
                data-test="challenge-basics-regions-name"
              >
                {region.name}
              </Col>
            );
          })}
        </Row>
      </IconCard>
      <IconCard title={tcommon('Topics')} icon={<TagsOutlined />}>
        <Row className="gap-2 mt-3">
          {challenge?.topics?.map((topic, index) => {
            return (
              <Col
                className="bg-zinc-50 py-1 px-3 rounded-2xl text-xs"
                key={index}
                data-test="challenge-basics-topics-name"
              >
                {topic.name}
              </Col>
            );
          })}
        </Row>
      </IconCard>
      {challenge.__typename === 'InternalChallenge' && (
        <IconCard
          title={t('intra_challenge_basics_registration_panel_members')}
          icon={<UserOutlined />}
        >
          <Typography.Text>
            {challenge?.panelMembers?.join(', ')}
          </Typography.Text>
        </IconCard>
      )}
      {challenge.__typename === 'InternalChallenge' && (
        <IconCard
          title={t('intra_challenge_basics_registration_business_areas')}
          icon={<UserOutlined />}
        >
          <ul className="list-disc">
            {businessArea?.dashboardChallengeIdeasByAreas?.map(
              (area, index) => (
                <li key={index}>
                  <Typography.Text>{area.name}</Typography.Text>
                </li>
              )
            )}
          </ul>
        </IconCard>
      )}
      {challenge.__typename === 'OpenChallenge' && showShareButtons && (
        <SocialShareButtonsGroup />
      )}
    </Space>
  );
}

export default ChallengeBasics;
